import {
  action$,
  actionCreator,
  reducer,
  reduceState,
  streamReducer,
} from '@ardoq/rxbeach';
import { context$ } from 'streams/context/context$';
import type { ContextShape, ScenarioModeState } from '@ardoq/data-model';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { combineLatest } from 'rxjs';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { type PermissionContext } from '@ardoq/access-control';
import { componentAccessControlOperation } from 'resourcePermissions/accessControlHelpers/component';
import { componentInterface } from '@ardoq/component-interface';
import { referenceAccessControlOperation } from 'resourcePermissions/accessControlHelpers/reference';
import { featureContext$ } from 'streams/featureContext$';
import { type FeatureContext } from '@ardoq/features';
import { referenceInterface } from '@ardoq/reference-interface';
import { activeScenario$ } from 'streams/activeScenario/activeScenario$';

export const switchTab = actionCreator<string>('[OPTIONS_MENU] switchTab');

export type OptionsMenuState = {
  tabId: string;
};

const initialState = { tabId: '' };

const mapContextToOptionMenuState = (
  state: OptionsMenuState,
  [contextState, permissionContext, activeScenarioState, featureContext]: [
    ContextShape,
    PermissionContext,
    ScenarioModeState,
    FeatureContext,
  ]
) => {
  if (state.tabId) return state;
  const { componentId, workspaceId, referenceId } = contextState;
  const canEditWorkspace = workspaceAccessControlInterface.canEditWorkspace(
    permissionContext,
    workspaceId,
    activeScenarioState
  );
  if (referenceId) {
    if (canEditWorkspace) {
      return { ...state, tabId: 'reference' };
    }
    const reference = referenceInterface.get(referenceId);
    const canEditReference =
      !!reference &&
      referenceAccessControlOperation.canEditReference({
        permissionContext,
        reference,
        sourceComponent: componentInterface.get(reference.source) ?? undefined,
        featureContext,
      });
    const tabId = canEditReference ? 'reference' : 'workspace';
    return { ...state, tabId };
  }

  if (componentId) {
    if (canEditWorkspace) {
      return { ...state, tabId: 'component' };
    }
    const componentData = componentInterface.get(componentId);
    if (!componentData) {
      return { ...state, tabId: 'workspace' };
    }
    const canEditComponentProperties =
      componentAccessControlOperation.canEditComponent({
        permissionContext,
        component: componentData,
        featureContext,
      });
    if (canEditComponentProperties) {
      return { ...state, tabId: 'component' };
    }
  }

  return { ...state, tabId: 'workspace' };
};

const contextReducer = streamReducer<
  OptionsMenuState,
  [ContextShape, PermissionContext, ScenarioModeState, FeatureContext]
>(
  combineLatest([
    context$,
    currentUserPermissionContext$,
    activeScenario$,
    featureContext$,
  ]),
  mapContextToOptionMenuState
);

const switchTabReducer = (state: OptionsMenuState, tabId: string) => ({
  ...state,
  tabId,
});
const actionReducer = reducer<OptionsMenuState, string>(
  switchTab,
  switchTabReducer
);

export const optionsMenu$ = action$.pipe(
  reduceState('optionsMenu$', initialState, [contextReducer, actionReducer])
);
